/**
 * Generates three output Excel files matching the structure produced by
 * PERSONAL_1_21.11.2025.XLSB macros, verified against Пример для макроса_6.xlsx.
 *
 * Table structures:
 *  1. "Задание на закупку"    — full purchase list (bars + sheets + hardware)
 *  2. "Задание на производство" — operations summary aggregated from Операция 1-4 columns
 *  3. "Таблица для загрузки в 1С" — hierarchical BOM with blade-adjusted lengths
 */

import * as XLSX from 'xlsx';
import type { MaterialResult } from '../cutting-calc/CalcResultView';
import type { ParsedSpec, SpecRow } from '../solidworks-parser';

// ─── Public types ─────────────────────────────────────────────────────────────

export interface GeneratedFile {
  name: string;
  /** base64-encoded xlsx — ready for BX24 crm.timeline.comment.add FILES[] */
  base64: string;
  /** Uint8Array for browser download via URL.createObjectURL */
  bytes: Uint8Array;
}

export interface AllFiles {
  purchase: GeneratedFile;
  production: GeneratedFile;
  onec: GeneratedFile;
}

// ─── Low-level helpers ────────────────────────────────────────────────────────

type Row = (string | number | null)[];

function makeSheet(rows: Row[], colWidths: number[]): XLSX.WorkSheet {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  ws['!cols'] = colWidths.map((wch) => ({ wch }));
  return ws;
}

function buildWb(sheets: Array<{ name: string; ws: XLSX.WorkSheet }>): XLSX.WorkBook {
  const wb = XLSX.utils.book_new();
  for (const { name, ws } of sheets) {
    XLSX.utils.book_append_sheet(wb, ws, name);
  }
  return wb;
}

function toFile(wb: XLSX.WorkBook, name: string): GeneratedFile {
  const base64: string = XLSX.write(wb, { type: 'base64', bookType: 'xlsx' });
  const bytes: Uint8Array = XLSX.write(wb, { type: 'array', bookType: 'xlsx' });
  return { name, base64, bytes };
}

function fmtDate(d: Date): string {
  return d.toLocaleDateString('ru-RU');
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

// ─── Domain helpers ───────────────────────────────────────────────────────────

/** Returns the set of row numbers that are assembly nodes (they have at least one child row). */
function getAssemblyNumbers(rows: SpecRow[]): Set<string> {
  const assemblies = new Set<string>();
  for (let i = 0; i < rows.length - 1; i++) {
    if (rows[i + 1].level > rows[i].level) {
      assemblies.add(rows[i].number);
    }
  }
  return assemblies;
}

/** Compute accumulated quantity (kvoStack product) for each SpecRow. */
function withTotalQty(rows: SpecRow[]): Array<SpecRow & { totalQty: number }> {
  const stack: number[] = [];
  return rows.map((row) => {
    stack.length = row.level;
    stack[row.level - 1] = row.qty;
    const totalQty = stack.reduce((a, b) => a * b, 1);
    return { ...row, totalQty };
  });
}

/**
 * Returns the correct quantity column value and unit for a given operation name.
 * Based on analysis of PERSONAL_1_21.11.2025.XLSB output.
 */
function opMetric(opName: string, row: SpecRow): { qty: number | null; unit: string } {
  const op = opName.toLowerCase();
  if (/лазерная резка/.test(op) || /резка трубы/.test(op))
    return { qty: row.cuttingContour, unit: 'м' };
  if (/гибка/.test(op))
    return { qty: row.bends, unit: 'шт.' };
  if (/сварка/.test(op))
    return { qty: row.weldLength, unit: 'мм' };
  if (/окраск/.test(op) || /грунтовк/.test(op))
    return { qty: row.surface, unit: 'м2' };
  if (/подготовк/.test(op) || /отгрузк/.test(op))
    return { qty: row.mass, unit: 'кг' };
  if (/лентопил/.test(op))
    return { qty: row.crossSection, unit: 'см2' };
  // Generic fallback — no known metric for this operation type
  return { qty: null, unit: '' };
}

// ─── 1. Задание на закупку ────────────────────────────────────────────────────

/**
 * Full purchase list:
 *  - Linear materials (м): bar count, bar length, total, % blade waste
 *  - Sheet materials (м2): listed with total area
 *  - Purchased components (no unit): listed with total count in шт.
 *
 * Matches column structure of Пример для макроса_6.xlsx "Задание на закупку":
 * № п/п | Материал | Кол-во заготовок, шт. | Длина заготовки, м |
 * Общее количество | Ед. изм. | % отходов
 */
export function buildPurchaseOrder(
  results: MaterialResult[],
  spec: ParsedSpec,
  dealId: string,
): GeneratedFile {
  const HEADER: Row = [
    '№ п/п', 'Материал',
    'Требуемое количество заготовок, шт.', 'Длина заготовки, м',
    'Общее количество материала', 'Ед. изм.', '% отходов',
  ];

  const rows: Row[] = [
    [`Задание на закупку. Сделка #${dealId}. Дата: ${fmtDate(new Date())}`],
    [],
    HEADER,
  ];

  let idx = 1;

  // ── Section 1: linear materials from calc results ─────────────────────────
  // Group usedStocks by (material, stockLength)
  const barMap = new Map<
    string,
    { material: string; stockLength: number; barCount: number; totalPiecesLen: number }
  >();
  for (const { material, result } of results) {
    for (const stock of result.usedStocks) {
      const key = `${material}||${stock.length}`;
      const existing = barMap.get(key);
      const piecesLen = stock.pieces.reduce((s, p) => s + p.length, 0);
      if (existing) {
        existing.barCount++;
        existing.totalPiecesLen += piecesLen;
      } else {
        barMap.set(key, { material, stockLength: stock.length, barCount: 1, totalPiecesLen: piecesLen });
      }
    }
  }

  for (const { material, stockLength, barCount, totalPiecesLen } of barMap.values()) {
    const stockLenM = round4(stockLength / 1000);
    const totalM = round4(barCount * stockLenM);
    const totalStockLen = barCount * stockLength;
    const wastePercent = totalStockLen > 0
      ? round4((totalStockLen - totalPiecesLen) / totalStockLen * 100)
      : 0;
    rows.push([idx++, material, barCount, stockLenM, totalM, 'м', wastePercent]);
  }

  // ── Section 2: non-linear materials from spec ──────────────────────────────
  // Aggregate by material name + unit, skipping linear (м) already covered above
  const coveredLinear = new Set(results.map((r) => r.material));
  const nonLinearMap = new Map<string, { material: string; unit: string; total: number }>();

  for (const row of withTotalQty(spec.rows)) {
    if (!row.material || !row.unit || row.materialQty == null) continue;
    if (row.unit === 'м' && coveredLinear.has(row.material)) continue; // already in section 1
    const key = `${row.material}||${row.unit}`;
    const totalContrib = row.materialQty * row.totalQty;
    const existing = nonLinearMap.get(key);
    if (existing) {
      existing.total += totalContrib;
    } else {
      nonLinearMap.set(key, { material: row.material, unit: row.unit, total: totalContrib });
    }
  }

  for (const { material, unit, total } of nonLinearMap.values()) {
    rows.push([idx++, material, null, null, round4(total), unit, null]);
  }

  // ── Section 3: purchased components (no material / no unit, leaf nodes only) ─
  // Assembly nodes (those with child rows) are not purchased items — skip them.
  const assemblyNumbers = getAssemblyNumbers(spec.rows);
  const purchasedMap = new Map<string, { name: string; total: number }>();
  for (const row of withTotalQty(spec.rows)) {
    if (row.material || row.unit) continue;
    if (!row.name || row.level < 2) continue;
    if (assemblyNumbers.has(row.number)) continue;
    const key = row.name;
    const existing = purchasedMap.get(key);
    if (existing) {
      existing.total += row.totalQty;
    } else {
      purchasedMap.set(key, { name: row.name, total: row.totalQty });
    }
  }

  for (const { name, total } of purchasedMap.values()) {
    rows.push([idx++, name, null, null, total, 'шт.', null]);
  }

  const ws = makeSheet(rows, [6, 50, 28, 20, 26, 10, 10]);
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

  return toFile(buildWb([{ name: 'Задание на закупку', ws }]), 'Задание на закупку.xlsx');
}

// ─── 2. Задание на производство ───────────────────────────────────────────────

/**
 * Operations summary — aggregates Операция 1-4 columns across all spec rows,
 * weighted by totalQty. Each operation maps to its physical quantity column.
 *
 * Matches column structure:
 * № | Технологическая операция | Количество | Ед.изм.
 */
export function buildProductionOrder(
  spec: ParsedSpec,
  dealId: string,
): GeneratedFile {
  const HEADER: Row = ['№', 'Технологическая операция', 'Количество', 'Ед.изм.'];

  // Accumulate: op name → { total, unit }
  const opMap = new Map<string, { total: number; unit: string }>();

  for (const row of withTotalQty(spec.rows)) {
    for (const opName of [row.op1, row.op2, row.op3, row.op4]) {
      if (!opName) continue;
      const { qty, unit } = opMetric(opName, row);
      if (qty == null || qty === 0) continue;
      const contrib = qty * row.totalQty;
      const existing = opMap.get(opName);
      if (existing) {
        existing.total += contrib;
      } else {
        opMap.set(opName, { total: contrib, unit });
      }
    }
  }

  const dataRows: Row[] = [];
  let idx = 1;
  for (const [opName, { total, unit }] of opMap.entries()) {
    dataRows.push([idx++, opName, round4(total), unit]);
  }

  const tableRows: Row[] = [
    [`Задание на производство. Сделка #${dealId}. Дата: ${fmtDate(new Date())}`],
    [],
    HEADER,
    ...dataRows,
  ];

  const ws = makeSheet(tableRows, [4, 60, 16, 12]);
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 3 } }];

  return toFile(buildWb([{ name: 'Задание на производство', ws }]), 'Задание на производство.xlsx');
}

// ─── 3. Таблица для загрузки в 1С ────────────────────────────────────────────

/**
 * Full hierarchical BOM with quantities, mirroring "Преобразованная таблица"
 * from the macro output. For linear materials, lengths are adjusted by
 * proportional blade waste to get actual material consumption per piece.
 *
 * Structure per spec row:
 *   Main row: № | Артикул | Наименование | qty | шт.
 *   Material sub-row: "" | "" | material | adjustedQty | unit
 *   Op sub-rows: "" | "" | opName | opQty | opUnit
 *
 * Columns: № | Артикул | Наименование | Количество | Ед. изм.
 */
export function build1cTable(
  results: MaterialResult[],
  spec: ParsedSpec,
  _blade: number,
  dealId: string,
): GeneratedFile {
  const HEADER: Row = ['№', 'Артикул', 'Наименование', 'Количество', 'Ед. изм.'];

  // Precompute blade waste factor per material (bladeWaste / totalPiecesLen)
  const bladeFactor = new Map<string, number>();
  for (const { material, result } of results) {
    let totalBlade = 0;
    let totalPieces = 0;
    for (const stock of result.usedStocks) {
      totalBlade += stock.bladeWaste;
      totalPieces += stock.pieces.reduce((s, p) => s + p.length, 0);
    }
    bladeFactor.set(material, totalPieces > 0 ? totalBlade / totalPieces : 0);
  }

  const dataRows: Row[] = [];

  for (const row of spec.rows) {
    const pad = '\t'.repeat(row.level - 1);   // "1"=0 tabs, "1.1"=1, "1.1.1"=2, …
    const subPad = '\t'.repeat(row.level);     // sub-rows (material, op) are one level deeper

    // Main part row
    dataRows.push([
      pad + row.number,
      row.article ? pad + row.article : null,
      row.name    ? pad + row.name    : null,
      row.qty,
      'шт.',
    ]);

    // Material sub-row
    if (row.material && row.unit && row.materialQty != null) {
      let qty = row.materialQty;
      // Adjust linear material length by blade waste factor
      if (row.unit === 'м') {
        const factor = bladeFactor.get(row.material) ?? 0;
        qty = round4(qty * (1 + factor));
      }
      dataRows.push([null, null, subPad + row.material, qty, row.unit]);
    }

    // Operation sub-rows
    for (const opName of [row.op1, row.op2, row.op3, row.op4]) {
      if (!opName) continue;
      const { qty, unit } = opMetric(opName, row);
      if (qty != null && qty > 0) {
        dataRows.push([null, null, subPad + opName, qty, unit]);
      }
    }
  }

  const tableRows: Row[] = [
    [`Таблица для загрузки в 1С. Сделка #${dealId}. Дата: ${fmtDate(new Date())}`],
    [],
    HEADER,
    ...dataRows,
  ];

  const ws = makeSheet(tableRows, [12, 40, 60, 16, 10]);
  ws['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];

  return toFile(buildWb([{ name: 'Таблица для загрузки в 1С', ws }]), 'Таблица для загрузки в 1С.xlsx');
}

// ─── Public entry point ───────────────────────────────────────────────────────

export function generateAllFiles(
  results: MaterialResult[],
  spec: ParsedSpec,
  blade: number,
  trim: number,
  dealId: string,
): AllFiles {
  void trim; // trim is captured in calc results via stock.trimWaste — not needed here separately
  return {
    purchase:   buildPurchaseOrder(results, spec, dealId),
    production: buildProductionOrder(spec, dealId),
    onec:       build1cTable(results, spec, blade, dealId),
  };
}
